"use client";

import mammoth from "mammoth";
import React, { ErrorInfo, useEffect, useRef } from "react";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

type ErrorBoundaryProps = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
};

class ErrorBoundary extends React.Component<ErrorBoundaryProps, State> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  async componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Uncaught error:", error, errorInfo);

    // // You can also log the error to an error reporting service
    // // logErrorToMyService(error, errorInfo);
    // Sentry.captureException(error);
    // await Sentry.flush();
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div>
          <p>There was an error displaying the Word document.</p>
        </div>
      );
    }

    return this.props.children;
  }
}

interface Props {
  file: File;
  // numPages: number;
  // containerRef: HTMLElement | null;
  // containerWidth: number;
}

const DisplayWordFile: React.FC<Props> = ({ file }) => {
  return (
    <ErrorBoundary>
      <DisplayWordFileInner file={file} />
    </ErrorBoundary>
  );
};

const DisplayWordFileInner: React.FC<Props> = ({ file }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const renderFile = async (
    file: File,
    containerRef: React.RefObject<HTMLDivElement | null>
  ) => {
    const buffer = await file.arrayBuffer();
    mammoth.convertToHtml({ arrayBuffer: buffer }).then((result) => {
      const html = result.value; // The generated HTML
      const messages = result.messages; // Any messages, such as warnings during conversion
      if (containerRef.current) {
        containerRef.current.innerHTML = html;
      }
    });
  };

  useEffect(() => {
    if (containerRef.current) {
      renderFile(file, containerRef);
    }
  }, [containerRef]);

  return (
    <div className="Example sticky top-20 h-max w-full max-w-lg grow-0 min-xl:max-w-2xl min-lg:max-w-xl">
      <header>
        <h4>{file.name}</h4>
      </header>

      <div className="group relative overflow-hidden rounded shadow-2xl">
        <div
          ref={containerRef}
          className="max-h-[750px] overflow-auto rounded bg-white shadow-lg"
        >
          <h1>...</h1>
        </div>
      </div>
    </div>
  );
};

export default DisplayWordFile;
